import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatsCircle, MagnifyingGlass, CalendarBlank, X, FileText, Wrench, Microphone, ChatCenteredDots, CurrencyDollar } from '@phosphor-icons/react';

import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { ConversationRows } from '../components/admin/conversation-view';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { useUrlPagination } from '../hooks/use-url-pagination';
import { ROLE_LABEL, useRoles } from '../hooks/use-roles';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';
import { useLocalStorage } from '../hooks/use-local-storage';
import { AUDIT_TIME_ZONE_LABEL, endOfAuditDayUtc, startOfAuditDayUtc } from '../lib/audit-time';
import { deleteThread } from '../lib/admin';
import {
  COMMENT_KINDS,
  SIGNALS,
  kpiBand,
  fetchConversationModes,
  fetchConversationSummary,
  fetchConversations,
  splitSearch,
  surfaceOf,
  type AdminConversationFilter,
  type AdminConversationRow,
  type AdminConversationSummary,
} from '../lib/admin-conversations';

const HEADER_ICON = <ChatsCircle size={26} />;
const PAGE_SIZE_KEY = 'naia:admin:conversations:page-size';
const SORT_KEY = 'naia:admin:conversations:sort';

function KpiCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4 shadow-[var(--naia-shadow-card)]">
      <div className="flex items-center gap-2 text-[var(--naia-text-secondary)]">
        <span className="text-[var(--naia-blue)]">{icon}</span>
        <span className="text-[11px] font-black uppercase tracking-[0.7px]">{label}</span>
      </div>
      <div className="text-[22px] font-extrabold leading-tight text-[var(--naia-text-primary)]">{value}</div>
      {sub && <div className="text-[11.5px] text-[var(--naia-text-secondary)]">{sub}</div>}
    </div>
  );
}

export default function AdminConversations() {
  const isAdminViewer = useIsAdminViewer();
  const { activeRole } = useRoles();
  const { page, setPage, totalPages } = useUrlPagination();
  const [pageSize, setPageSize] = useLocalStorage<number>(PAGE_SIZE_KEY, 10);
  const [sort, setSort] = useLocalStorage<AdminConversationFilter['sort']>(SORT_KEY, 'recent');

  const [items, setItems] = useState<AdminConversationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modes, setModes] = useState<string[]>([]);
  const [summary, setSummary] = useState<AdminConversationSummary | null>(null);

  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('');
  const [signal, setSignal] = useState('');
  const [hasVoice, setHasVoice] = useState(false);
  const [reviewed, setReviewed] = useState<'' | 'yes' | 'no'>('');
  const [commentKind, setCommentKind] = useState('');
  const [sinceIso, setSinceIso] = useState('');
  const [untilIso, setUntilIso] = useState('');

  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filter = useMemo<AdminConversationFilter>(() => {
    const { ids, text } = splitSearch(search);
    return {
      search: text || undefined,
      ids: ids.length > 0 ? ids : undefined,
      mode: mode || undefined,
      signal: (signal || undefined) as AdminConversationFilter['signal'],
      hasVoice: hasVoice || undefined,
      reviewed: (reviewed || undefined) as AdminConversationFilter['reviewed'],
      commentKind: (commentKind || undefined) as AdminConversationFilter['commentKind'],
      sinceIso: sinceIso ? (startOfAuditDayUtc(sinceIso)?.toISOString() ?? undefined) : undefined,
      untilIso: untilIso ? (endOfAuditDayUtc(untilIso)?.toISOString() ?? undefined) : undefined,
      sort: sort ?? 'recent',
    };
  }, [search, mode, signal, hasVoice, reviewed, commentKind, sinceIso, untilIso, sort]);

  const load = useCallback(
    (f: AdminConversationFilter, p: number, limit: number) => {
      setLoading(true);
      setError(null);
      fetchConversations({ ...f, page: p, limit })
        .then((r) => {
          setItems(r.items);
          setTotal(r.total);
          if (p > Math.max(1, Math.ceil(r.total / limit))) setPage(1);
        })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    },
    [setPage],
  );

  useEffect(() => {
    fetchConversationModes().then(setModes).catch(() => {});
  }, []);

  /* La synthèse ne dépend NI de la page NI du signal sélectionné : elle décrit la
     population que le filtre découpe, pas la tranche qu'on regarde. Elle ne se
     recharge donc pas quand on tourne les pages. */
  useEffect(() => {
    let vivant = true;
    fetchConversationSummary(filter)
      .then((r) => vivant && setSummary(r))
      .catch(() => vivant && setSummary(null));
    return () => {
      vivant = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [search, mode, hasVoice, reviewed, sinceIso, untilIso]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setPage(1);
      load(filter, 1, pageSize);
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter]);

  useEffect(() => {
    load(filter, page, pageSize);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [page, pageSize]);

  const handleDelete = useCallback((row: AdminConversationRow) => {
    const label = row.title?.trim() || row.preview?.trim() || row.id;
    if (!window.confirm(`Supprimer la conversation « ${label} » et tous ses messages ?`)) return;
    setError(null);
    deleteThread(row.id)
      .then(() => {
        setItems((prev) => prev.filter((r) => r.id !== row.id));
        setTotal((t) => Math.max(0, t - 1));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const pages = totalPages(total, pageSize);
  const hasFilters = !!(search || mode || signal || hasVoice || reviewed || commentKind || sinceIso || untilIso);
  /* TOUS les chiffres du bandeau viennent d'ici, et d'ici seulement : la
     signature de `kpiBand` ne donne pas accès à `total`, qui est le périmètre de
     la LISTE et non celui de la synthèse. C'est ce qui rend le mélange
     inécrivable plutôt que détectable après coup. */
  const band = summary ? kpiBand(summary) : null;

  const reset = () => {
    setSearch('');
    setMode('');
    setSignal('');
    setHasVoice(false);
    setReviewed('');
    setCommentKind('');
    setSinceIso('');
    setUntilIso('');
  };

  return (
    <>
      <Header chromeless />
      <HeaderContent width="xl" showBurger backTo="/admin" backLabel="Admin" icon={HEADER_ICON}>
        <div className="flex min-w-0 items-center gap-2.5">
          <h1 className={HEADER_TITLE_CLASS}>Conversations</h1>
          <span className={HEADER_BADGE_CLASS}>{ROLE_LABEL[activeRole ?? 'admin']}</span>
        </div>
      </HeaderContent>

      <main className="mx-auto max-w-[1400px] px-4 pt-4 pb-16">
        {/* TOUS CES NOMBRES PORTENT LE MEME PERIMETRE : celui du filtre, pas
            celui de la page. La version precedente affichait un total de periode
            a cote de trois comptes calcules sur les dix lignes visibles, et ne le
            disait qu'en petit — deux perimetres dans le meme bandeau, ce qui est
            la definition d'une mesure trompeuse. */}
        <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <KpiCard
            icon={<ChatsCircle size={16} />}
            label="Conversations"
            value={band ? band.conversations.toLocaleString('fr-FR') : '—'}
            sub="sur la période filtrée"
          />
          <KpiCard
            icon={<FileText size={16} />}
            label="Tours d'assistant"
            value={band ? band.assistantTurns.toLocaleString('fr-FR') : '—'}
            sub="sur la période filtrée"
          />
          <KpiCard
            icon={<Wrench size={16} />}
            label="Appels d'outils"
            value={band ? band.toolCalls.toLocaleString('fr-FR') : '—'}
            sub={band ? `dont ${band.toolErrors.toLocaleString('fr-FR')} en erreur` : 'sur la période filtrée'}
          />
          <KpiCard
            icon={<ChatCenteredDots size={16} />}
            label="Conversations relues"
            value={band ? band.reviewed.toLocaleString('fr-FR') : '—'}
            sub={
              band
                ? `${band.comments.toLocaleString('fr-FR')} commentaire(s) · ${band.unreviewed.toLocaleString(
                    'fr-FR',
                  )} sans relecture`
                : 'commentaires vivants'
            }
          />
          <KpiCard
            icon={<CurrencyDollar size={16} />}
            label="Coût"
            value={band && band.costUsd !== null ? `$${band.costUsd.toFixed(2)}` : '—'}
            /* Jamais « $0 » quand des lignes ne sont pas tarifées : un zéro se
               lirait comme un appel gratuit. */
            sub={
              band && band.unpricedUsage > 0
                ? `${band.unpricedUsage} ligne(s) sans tarif — plancher`
                : 'sur la période filtrée'
            }
          />
        </div>

        {/* Chaque compteur est un FILTRE : voir un nombre et l'ouvrir est le
            geste que cet écran doit rendre possible en un clic. */}
        {summary && (
          <div className="mb-3 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] px-3 py-2.5 shadow-[var(--naia-shadow-card)]">
            {/* LA FILE DE TRAVAIL, en premier : c'est le seul bouton qui répond à
                « qu'est-ce qu'il me reste à faire ». Le reste de ce bandeau
                décrit ; celui-ci met au travail. */}
            <div className="mb-2.5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setReviewed(reviewed === 'no' ? '' : 'no')}
                className={`min-h-[40px] rounded-lg border px-3.5 py-1.5 text-[13px] font-bold transition ${
                  reviewed === 'no'
                    ? 'border-[var(--naia-blue)] bg-[var(--naia-info-bg)] text-[var(--naia-text-primary)]'
                    : 'border-[var(--naia-line)] bg-[var(--naia-card)] text-[var(--naia-text-primary)] hover:border-[var(--naia-blue)]'
                }`}
              >
                À relire
                <span className="ml-2 font-black">
                  {band ? band.unreviewed.toLocaleString('fr-FR') : '—'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setReviewed(reviewed === 'yes' ? '' : 'yes')}
                className={`min-h-[40px] rounded-lg border px-3.5 py-1.5 text-[13px] font-semibold transition ${
                  reviewed === 'yes'
                    ? 'border-[var(--naia-blue)] bg-[var(--naia-info-bg)] text-[var(--naia-text-primary)]'
                    : 'border-[var(--naia-line)] bg-[var(--naia-card)] text-[var(--naia-text-secondary)] hover:border-[var(--naia-blue)]'
                }`}
              >
                Déjà relues
                <span className="ml-2 font-black text-[var(--naia-text-primary)]">
                  {band ? band.reviewed.toLocaleString('fr-FR') : '—'}
                </span>
              </button>
              {reviewed !== '' && (
                <button
                  type="button"
                  onClick={() => setReviewed('')}
                  className="min-h-[40px] text-[12.5px] font-semibold text-[var(--naia-text-secondary)] underline underline-offset-2"
                >
                  toutes
                </button>
              )}
            </div>

            {/* CE QUE CETTE FILE NE COUVRE PAS. « À relire : toutes » promet une
                exhaustivité qu'aucun compteur ne possède : il faut dire sur
                quelle fenêtre et sur quelle population. Et la borne du
                23/08 n'est pas décorative — avant elle, les résultats d'outils
                ne sont pas enregistrés, donc « la recherche a-t-elle trouvé ? »
                est INDÉCIDABLE, jamais « non ». */}
            <div className="mb-2.5 text-[11.5px] leading-snug text-[var(--naia-text-secondary)]">
              Périmètre :{' '}
              {sinceIso || untilIso
                ? `du ${sinceIso || 'début'} au ${untilIso || "aujourd'hui"} (${AUDIT_TIME_ZONE_LABEL})`
                : 'toutes dates'}
              {' · '}
              {mode ? surfaceOf({ mode }).label : 'toutes surfaces'}
              {hasVoice && ' · avec vocal'}
              {' · '}
              {band ? band.assistantTurns.toLocaleString('fr-FR') : '—'} tours d'assistant examinés.
              {' '}
              Avant le 23/08/2026 15 h 25 UTC, les résultats d'outils ne sont pas enregistrés :
              sur ces conversations, « la recherche a-t-elle trouvé ? » est indécidable, pas négative.
            </div>

            <div className="mb-1.5 text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
              Signaux automatiques — cliquer pour filtrer
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SIGNALS.filter((sg) => (summary.by_signal[sg.id] ?? 0) > 0).map((sg) => (
                <button
                  key={sg.id}
                  type="button"
                  onClick={() => setSignal(signal === sg.id ? '' : sg.id)}
                  title={sg.help}
                  className={`min-h-[36px] rounded-full border px-3 py-1 text-[12px] font-semibold transition ${
                    signal === sg.id
                      ? 'border-[var(--naia-blue)] bg-[var(--naia-info-bg)] text-[var(--naia-text-primary)]'
                      : 'border-[var(--naia-line)] bg-[var(--naia-card)] text-[var(--naia-text-secondary)] hover:border-[var(--naia-blue)]'
                  }`}
                >
                  {sg.label}
                  <span className="ml-1.5 font-black text-[var(--naia-text-primary)]">
                    {(summary.by_signal[sg.id] ?? 0).toLocaleString('fr-FR')}
                  </span>
                </button>
              ))}
              {SIGNALS.every((sg) => (summary.by_signal[sg.id] ?? 0) === 0) && (
                <span className="text-[12.5px] text-[var(--naia-text-secondary)]">
                  Aucun signal sur ce périmètre.
                </span>
              )}
            </div>

            {band && band.comments > 0 && (
              <>
                <div className="mb-1.5 mt-3 text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
                  Relectures — ce qui a été jugé, et pourquoi
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {COMMENT_KINDS.filter((k) => (summary.by_comment_kind[k.id] ?? 0) > 0).map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => setCommentKind(commentKind === k.id ? '' : k.id)}
                      className={`min-h-[36px] rounded-full border px-3 py-1 text-[12px] font-semibold transition ${
                        commentKind === k.id
                          ? 'border-[var(--naia-blue)] bg-[var(--naia-info-bg)] text-[var(--naia-text-primary)]'
                          : 'border-[var(--naia-line)] bg-[var(--naia-sky)] text-[var(--naia-text-primary)] hover:border-[var(--naia-blue)]'
                      }`}
                    >
                      {k.label}
                      <span className="ml-1.5 font-black">
                        {(summary.by_comment_kind[k.id] ?? 0).toLocaleString('fr-FR')}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mb-3 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] px-3 py-2 shadow-[var(--naia-shadow-card)]">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <MagnifyingGlass
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Texte, e-mail, identifiant de fil ou de document, ou URL collée…"
                className="h-11 w-full pl-7 text-[12.5px]"
              />
            </div>

            <NativeSelect value={mode} onChange={(e) => setMode(e.target.value)} className="h-11 w-[170px] text-[12.5px]">
              <option value="">Toutes surfaces</option>
              {modes.map((m) => (
                <option key={m} value={m}>
                  {surfaceOf({ mode: m }).label}
                </option>
              ))}
            </NativeSelect>

            <NativeSelect value={signal} onChange={(e) => setSignal(e.target.value)} className="h-11 w-[210px] text-[12.5px]">
              <option value="">Tous les signaux</option>
              {SIGNALS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </NativeSelect>

            <NativeSelect
              value={sort ?? 'recent'}
              onChange={(e) => setSort(e.target.value as AdminConversationFilter['sort'])}
              className="h-11 w-[165px] text-[12.5px]"
            >
              <option value="recent">Plus récentes</option>
              <option value="signals">Plus signalées</option>
              <option value="messages">Plus de messages</option>
              <option value="tools">Plus d'outils</option>
              <option value="cost">Plus coûteuses</option>
            </NativeSelect>

            <button
              type="button"
              onClick={() => setHasVoice((v) => !v)}
              aria-pressed={hasVoice}
              title="Fils qui hebergent au moins une session vocale (usage_records.kind = 'voice')"
              className={`inline-flex h-11 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-semibold transition-colors ${
                hasVoice
                  ? 'border-[var(--naia-tertiary)] bg-[var(--naia-warn-bg)] text-[var(--naia-tertiary)]'
                  : 'border-[var(--naia-line)] text-[var(--naia-text-secondary)] hover:border-[var(--naia-blue)] hover:text-[var(--naia-blue)]'
              }`}
            >
              <Microphone size={13} />
              Avec vocal
            </button>

            <div className="flex items-center gap-1 text-[12px] text-[var(--naia-text-secondary)]">
              <CalendarBlank size={13} className="shrink-0" />
              <input
                type="date"
                aria-label="Depuis"
                value={sinceIso}
                onChange={(e) => setSinceIso(e.target.value)}
                className="h-11 rounded-lg border border-[var(--naia-line)] bg-[var(--naia-card)] px-1.5 text-[12px] text-[var(--naia-text-primary)]"
              />
              <span className="opacity-50">→</span>
              <input
                type="date"
                aria-label="Jusqu'à"
                value={untilIso}
                onChange={(e) => setUntilIso(e.target.value)}
                className="h-11 rounded-lg border border-[var(--naia-line)] bg-[var(--naia-card)] px-1.5 text-[12px] text-[var(--naia-text-primary)]"
              />
            </div>

            {hasFilters && (
              <button
                type="button"
                onClick={reset}
                title="Réinitialiser les filtres"
                aria-label="Réinitialiser les filtres"
                className="grid h-11 w-11 place-items-center rounded-lg border border-[var(--naia-line)] text-[var(--naia-text-secondary)] transition-colors hover:border-[var(--naia-blue)] hover:text-[var(--naia-blue)]"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between text-[12px] text-[var(--naia-text-secondary)]">
          {/* CE COMPTE EST CELUI DE LA LISTE, et il diffère légitimement de celui
              du bandeau dès qu'un signal ou une nature est actif — le bandeau
              décrit la population, ces puces la restreignent. Une différence
              légitime doit se DIRE, sinon elle se lit comme une contradiction :
              « 177 » en haut et « 12 » en bas sans un mot, c'est ce que cet écran
              a été repris pour ne plus faire. */}
          <span>
            {loading
              ? 'Chargement…'
              : band && band.conversations !== total
                ? `${total.toLocaleString('fr-FR')} sur ${band.conversations.toLocaleString('fr-FR')} conversations`
                : `${total.toLocaleString('fr-FR')} conversation${total > 1 ? 's' : ''}`}
          </span>
          {signal && <span>Filtré sur « {SIGNALS.find((s) => s.id === signal)?.label} »</span>}
          {commentKind && <span>Jugées « {COMMENT_KINDS.find((k) => k.id === commentKind)?.label} »</span>}
          {reviewed === 'no' && <span>Non relues seulement</span>}
          {reviewed === 'yes' && <span>Déjà relues seulement</span>}
        </div>

        {error && (
          <div className="mb-3 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-error-bg)] px-4 py-3 text-[13px] text-[var(--naia-error)]">
            {error}
          </div>
        )}

        <ConversationRows items={items} loading={loading} onDelete={isAdminViewer ? handleDelete : undefined} />

        <div className="flex flex-wrap items-center justify-between gap-3 text-[12.5px]">
          <span className="text-[var(--naia-text-secondary)]">
            Page {page} / {pages}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-[var(--naia-text-secondary)]">Lignes</span>
              <NativeSelect
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="min-h-[44px] w-[70px] text-[12px]"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </NativeSelect>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                Précédent
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= pages}>
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
